import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { ParcelCard } from 'components/ParcelCard';
import { TripCard } from 'components/TripCard';
import { EmptyState, ListSkeleton } from 'components/EmptyState';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from 'components/ui/tooltip';
import api from 'lib/api';
import { toast } from 'sonner';
import { Search, Package, Plane, Plus, BellPlus } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

export default function ListingsPage({ kind = 'deliveries', mineDefault = true }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(mineDefault);
  const [from, setFrom] = useState(params.get('departure_city') || '');
  const [to, setTo] = useState(params.get('arrival_city') || '');
  const fetchRef = useRef(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (mine) q.set('mine', 'true');
    if (from) q.set('departure_city', from);
    if (to) q.set('arrival_city', to);
    try {
      const r = await api.get(`/${kind === 'deliveries' ? 'deliveries' : 'trips'}?${q.toString()}`);
      setItems(r.data);
    } catch {}
    finally { setLoading(false); }
  }, [mine, from, to, kind]);

  const saveSearch = async () => {
    try {
      await api.post('/saved-searches', {
        kind: kind === 'deliveries' ? 'deliveries' : 'trips',
        departure_city: from || undefined,
        arrival_city: to || undefined,
      });
      toast.success(t('savedSearch.alertSaved'));
    } catch {
      toast.error(t('savedSearch.alertSaveFailed'));
    }
  };

  useEffect(() => { fetchRef.current = fetch; }, [fetch]);
  useEffect(() => { fetchRef.current?.(); }, [mine, kind]);

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">
            {kind === 'deliveries'
              ? (mine ? t('listings.title.myParcels') : t('listings.title.availableParcels'))
              : (mine ? t('listings.title.myTrips') : t('listings.title.availableTrips'))}
          </h1>
          <p className="text-muted-foreground">
            {items.length} {t(items.length === 1 ? 'listings.count.one' : 'listings.count.many')}
          </p>
        </div>
        <Button onClick={() => navigate(kind === 'deliveries' ? '/deliveries/new' : '/trips/new')} data-testid="new-item-button">
          <Plus size={16} className="mr-1" /> {kind === 'deliveries' ? t('listings.newParcel') : t('listings.newTrip')}
        </Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
          <Tabs value={mine ? 'mine' : 'all'} onValueChange={(v) => setMine(v === 'mine')} className="shrink-0">
            <TabsList>
              <TabsTrigger value="mine" data-testid="tab-mine">
                {kind === 'deliveries' ? t('listings.tab.myParcels') : t('listings.tab.myTrips')}
              </TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">{t('listings.tab.all')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Input placeholder={t('listings.filter.departure')} value={from} onChange={e => setFrom(e.target.value)} data-testid="filter-from-input" />
            <Input placeholder={t('listings.filter.arrival')} value={to} onChange={e => setTo(e.target.value)} data-testid="filter-to-input" />
            <div className="flex flex-col sm:flex-row gap-2 sm:col-span-2">
              <Button variant="secondary" onClick={fetch} data-testid="filter-search-button" className="flex-1"><Search size={14} className="mr-1" /> {t('listings.search')}</Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={saveSearch} type="button" data-testid="filter-create-alert-button" className="flex-1"><BellPlus size={14} className="mr-1" /> {t('savedSearch.createAlert')}</Button>
                </TooltipTrigger>
                <TooltipContent>
                  {kind === 'deliveries' ? t('listings.tooltip.parcels') : t('listings.tooltip.trips')}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          </div>
        </CardContent>
      </Card>

      {loading ? <ListSkeleton /> : items.length === 0 ? (
        <EmptyState
          icon={kind === 'deliveries' ? Package : Plane}
          title={mine
            ? (kind === 'deliveries' ? t('listings.empty.noParcels') : t('listings.empty.noTrips'))
            : t('listings.empty.noResults')}
          description={mine
            ? (kind === 'deliveries' ? t('listings.empty.createFirstParcel') : t('listings.empty.createFirstTrip'))
            : t('listings.empty.adjustFilters')}
          action={mine && <Button onClick={() => navigate(kind === 'deliveries' ? '/deliveries/new' : '/trips/new')}>{t('listings.create')}</Button>}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(item => kind === 'deliveries'
            ? <ParcelCard key={item.id} parcel={item} />
            : <TripCard key={item.id} trip={item} />)}
        </div>
      )}
    </DashboardShell>
  );
}
